import { JUICY_RAW_TYPE_FILTER_OPTIONS } from "./juicyFinanceClassifications.jsx";

function normalizeClassKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

/** Spiegel von backend/logic/tax-routing.js — nicht manuell überschreibbar. */
const LIQUIDITY_TAX_CLASSES = new Set([
  "liquidity_add_out",
  "liquidity_remove_lp_out",
  "liquidity_add_lp_in",
  "liquidity_remove_in",
]);

const VTHO_SECTION_ONLY_INCOME = new Set(["generated", "vtho_synthetic"]);

export const MANUAL_OVERRIDE_BLOCKED_CLASSES = new Set([
  ...LIQUIDITY_TAX_CLASSES,
  ...VTHO_SECTION_ONLY_INCOME,
]);

export function isManualOverrideClassBlocked(classKey) {
  const key = normalizeClassKey(classKey);
  if (!key || key === "auto") return false;
  return MANUAL_OVERRIDE_BLOCKED_CLASSES.has(key);
}

/** @deprecated Import from ../utils/manualOverrideUi.js */
export { validateManualOverrideClassificationForUi } from "../utils/manualOverrideUi.js";

function labelPair(de, en) {
  return { de, en };
}

/** Ebene 1 — häufige steuerliche Korrekturen (immer oben). */
export const MANUAL_OVERRIDE_TIER1 = [
  ["auto", labelPair("Auto-Klassifizierung", "Auto classification")],
  ["ignore", labelPair("Ignorieren / von Berechnung ausschließen", "Ignore / Exclude from calculation")],
  ["received", labelPair("Empfangen / Zufluss", "Received / Inflow")],
  ["sent", labelPair("Gesendet / Abfluss", "Sent / Outflow")],
  ["internal_transfer", labelPair("Interner Transfer", "Internal Transfer")],
  ["swap_in", labelPair("Swap In", "Swap In")],
  ["swap_out", labelPair("Swap Out", "Swap Out")],
  ["swap_refund_in", labelPair("Swap Refund In", "Swap Refund In")],
  ["x2earn_reward", labelPair("X2Earn Reward · Claim", "X2Earn Reward · Claim")],
  ["governance_reward", labelPair("Governance Reward", "Governance Reward")],
  ["staking_reward", labelPair("Staking Reward", "Staking Reward")],
  ["gas_fee", labelPair("Gas Fee", "Gas Fee")],
  ["gas_abstraction_fee", labelPair("Gas Payment · Paymaster Fee", "Gas Payment · Paymaster Fee")],
  ["nft_mint", labelPair("NFT Mint", "NFT Mint")],
  ["sale_payment", labelPair("NFT Sale", "NFT Sale")],
  ["refund", labelPair("Refund", "Refund")],
];

/** Wrap/Unwrap — 1:1-Transforms (neutral, FIFO-relevant). Keys = backend TRANSFORM_REGISTRY. */
export const MANUAL_OVERRIDE_WRAP_TRANSFORMS = [
  ["b3tr_vot3_wrap", labelPair("B3TR → VOT3 Wrap", "B3TR → VOT3 Wrap")],
  ["vot3_b3tr_wrap", labelPair("VOT3 → B3TR Wrap", "VOT3 → B3TR Wrap")],
  ["b3tr_vot3_unwrap", labelPair("B3TR → VOT3 Unwrap", "B3TR → VOT3 Unwrap")],
  ["vot3_b3tr_unwrap", labelPair("VOT3 → B3TR Unwrap", "VOT3 → B3TR Unwrap")],
  ["b3tr_veb3tr_wrap", labelPair("B3TR → VEB3TR Wrap", "B3TR → VEB3TR Wrap")],
  ["veb3tr_b3tr_wrap", labelPair("VEB3TR → B3TR Wrap", "VEB3TR → B3TR Wrap")],
  ["b3tr_veb3tr_unwrap", labelPair("B3TR → VEB3TR Unwrap", "B3TR → VEB3TR Unwrap")],
  ["veb3tr_b3tr_unwrap", labelPair("VEB3TR → B3TR Unwrap", "VEB3TR → B3TR Unwrap")],
  ["vot3_veb3tr_wrap", labelPair("VOT3 → VEB3TR Wrap", "VOT3 → VEB3TR Wrap")],
  ["veb3tr_vot3_wrap", labelPair("VEB3TR → VOT3 Wrap", "VEB3TR → VOT3 Wrap")],
  ["vot3_veb3tr_unwrap", labelPair("VOT3 → VEB3TR Unwrap", "VOT3 → VEB3TR Unwrap")],
  ["veb3tr_vot3_unwrap", labelPair("VEB3TR → VOT3 Unwrap", "VEB3TR → VOT3 Unwrap")],
  ["vet_vvet_wrap", labelPair("VET → VVET Wrap", "VET → VVET Wrap")],
  ["vvet_vet_unwrap", labelPair("VVET → VET Unwrap", "VVET → VET Unwrap")],
  ["vet_wvet_wrap", labelPair("VET → WVET Wrap", "VET → WVET Wrap")],
  ["wvet_vet_unwrap", labelPair("WVET → VET Unwrap", "WVET → VET Unwrap")],
  ["vet_bvet_wrap", labelPair("VET → BVET Wrap", "VET → BVET Wrap")],
  ["bvet_vet_unwrap", labelPair("BVET → VET Unwrap", "BVET → VET Unwrap")],
];

/** Ebene 2 — protokoll-/spezifische Klassen (statisch gepflegt). */
export const MANUAL_OVERRIDE_TIER2 = [
  ["x2earn_reward_claim", labelPair("X2Earn Reward · Claim", "X2Earn Reward · Claim")],
  ["stargate_staking_rewards", labelPair("Stargate Staking Rewards", "Stargate Staking Rewards")],
  ["stargate_boosted_maturity", labelPair("Stargate Boosted Maturity", "Stargate Boosted Maturity")],
  ["stargate_deposit", labelPair("Stargate Deposit", "Stargate Deposit")],
  ["thorhead_staking_rewards", labelPair("Thorhead Staking Rewards", "Thorhead Staking Rewards")],
  ["vemugs_staking_rewards", labelPair("VeMugs Staking Rewards", "VeMugs Staking Rewards")],
  ["vemugs_mint_reward", labelPair("VeMugs Mint Reward", "VeMugs Mint Reward")],
  ["vemugs_reward_distribution", labelPair("VeMugs Reward Distribution", "VeMugs Reward Distribution")],
  ["solarwise_electricity_revenue", labelPair("Solarwise Stromerlöse", "Solarwise Electricity Revenue")],
  ["solarwise_stake_deposit", labelPair("Solarwise Stake Deposit", "Solarwise Stake Deposit")],
  ["solarwise_stake_withdrawal", labelPair("Solarwise Stake Withdrawal", "Solarwise Stake Withdrawal")],
  ["verocket_generated_vtho_claim", labelPair("VeRocket VTHO Claim", "VeRocket VTHO Claim")],
  ["vexchange_farm_rewards", labelPair("Vexchange Farm Rewards", "Vexchange Farm Rewards")],
  ["betterswap_pool2earn_rewards", labelPair("BetterSwap Pool2Earn Rewards", "BetterSwap Pool2Earn Rewards")],
  ["mva_staking_rewards", labelPair("MVA Staking Rewards", "MVA Staking Rewards")],
  ["listen2earn_rewards", labelPair("Listen2Earn Rewards", "Listen2Earn Rewards")],
  ["wanchain_bridge_to_earn_reward", labelPair("Wanchain Bridge-to-Earn Reward", "Wanchain Bridge-to-Earn Reward")],
  ["bangzboardz_staking_reward", labelPair("BangzBoardz Staking Reward", "BangzBoardz Staking Reward")],
  ["goatz_milk_nft_holder_reward", labelPair("Goatz Club · MILK NFT Holder Reward", "Goatz Club · MILK NFT Holder Reward")],
  ["b3mo_challenge_escrow_deposit", labelPair("B3MO · Challenge Deposit", "B3MO · Challenge Deposit")],
  ["b3mo_challenge_principal_return", labelPair("B3MO · Principal Return", "B3MO · Principal Return")],
  ["b3mo_challenge_payout_reward", labelPair("B3MO · Challenge Reward", "B3MO · Challenge Reward")],
  ["b3mo_split_win_reward", labelPair("B3MO · SplitWin Reward", "B3MO · SplitWin Reward")],
  ["vet_domains_registration", labelPair("vet.domains · Registration", "vet.domains · Registration")],
  ["vet_domains_renewal", labelPair("vet.domains · Renewal", "vet.domains · Renewal")],
  ["vet_domains_payment_refund", labelPair("vet.domains · Payment Refund", "vet.domains · Payment Refund")],
  ["vaultx_loan_borrow", labelPair("VaultX · Loan Borrow", "VaultX · Loan Borrow")],
  ["vaultx_collateral_deposit", labelPair("VaultX · Collateral Deposit", "VaultX · Collateral Deposit")],
  ["vaultx_collateral_return", labelPair("VaultX · Collateral Return", "VaultX · Collateral Return")],
  ["vaultx_loan_principal_repayment", labelPair("VaultX · Principal Repayment", "VaultX · Principal Repayment")],
  ["vaultx_loan_repayment_fee", labelPair("VaultX · Repayment Fee", "VaultX · Repayment Fee")],
  ["vaultx_loan_repayment_refund", labelPair("VaultX · Refund", "VaultX · Refund")],
  ["vaultx_auction_bid_deposit", labelPair("VaultX · Auction Bid", "VaultX · Auction Bid")],
  ["vaultx_auction_bid_refund", labelPair("VaultX · Auction Bid Refund", "VaultX · Auction Bid Refund")],
  ["vaultx_protocol_outflow_review", labelPair("VaultX · Protocol Outflow Review", "VaultX · Protocol Outflow Review")],
  ["vaultx_nft_return_review", labelPair("VaultX · NFT Return Review", "VaultX · NFT Return Review")],
  ["wov_staking_rewards", labelPair("World of V Staking Rewards", "World of V Staking Rewards")],
  ["wov_genesis_staking_rewards", labelPair("World of V Staking Rewards (Genesis)", "World of V Staking Rewards (Genesis)")],
  ["wov_restake", labelPair("World of V Restake", "World of V Restake")],
  ["wov_claim_fee", labelPair("World of V Claim Fee", "World of V Claim Fee")],
  ["nft_marketplace_vet_bid_deposit", labelPair("World of V Auction Bid Deposit", "World of V Auction Bid Deposit")],
  ["nft_marketplace_vet_bid_refund", labelPair("World of V Auction Bid Refund", "World of V Auction Bid Refund")],
  ["nft_galaxy_mint", labelPair("NFT Galaxy Mint", "NFT Galaxy Mint")],
  ["nft_marketplace_purchase_payment", labelPair("NFT Marketplace-Kauf", "NFT marketplace purchase")],
  ["nfbc_purchase", labelPair("NFBC Purchase", "NFBC Purchase")],
  ["v3tax_purchase", labelPair("V3Tax purchase", "V3Tax purchase")],
  ["mugshot_pro_subscription", labelPair("Mugshot Pro Subscription", "Mugshot Pro Subscription")],
  ["vewin_4ocean_donation", labelPair("VeWin / 4ocean Spende", "VeWin / 4ocean Donation")],
  ["golden_empire_reward", labelPair("Golden Empire Reward", "Golden Empire Reward")],
  ["golden_empire_spend", labelPair("Golden Empire Spend", "Golden Empire Spend")],
  ["golden_empire_burn_spend", labelPair("Golden Empire Burn Spend", "Golden Empire Burn Spend")],
  ["vaiyans_vco_game_entry", labelPair("Vaiyans VCO Game Entry", "Vaiyans VCO Game Entry")],
  ["velottery_ticket_purchase", labelPair("VeLottery Ticket Purchase", "VeLottery Ticket Purchase")],
  ["scoopup_puppy_pass_fee", labelPair("ScoopUp Puppy Pass Fee", "ScoopUp Puppy Pass Fee")],
  ["pause_your_carbon_carbon_years", labelPair("Pause Your Carbon Carbon Years", "Pause Your Carbon Carbon Years")],
  ["node_legacy_rewards", labelPair("Legacy Node Rewards", "Legacy Node Rewards")],
  ["foundation_bonus", labelPair("Foundation Bonus", "Foundation Bonus")],
  ["safehaven_staking_rewards", labelPair("Safehaven Staking Rewards", "Safehaven Staking Rewards")],
  ["sagaz_reward_claim", labelPair("Sagaz Reward Claim", "Sagaz Reward Claim")],
  ["squad_game_reward", labelPair("Squirtle Squad Game Reward", "Squirtle Squad Game Reward")],
  ["squad_game_spend", labelPair("Squirtle Squad Game Spend", "Squirtle Squad Game Spend")],
  ["squad_nft_upgrade_spend", labelPair("Squirtle Squad NFT Upgrade Spend", "Squirtle Squad NFT Upgrade Spend")],
  ["squad_nft_upgrade_burn", labelPair("Squirtle Squad NFT Upgrade Burn", "Squirtle Squad NFT Upgrade Burn")],
  ["squad_nft_upgrade_mint", labelPair("Squirtle Squad NFT Upgrade Mint", "Squirtle Squad NFT Upgrade Mint")],
  ["squad_preico_payment", labelPair("Squirtle Squad Pre-ICO Payment", "Squirtle Squad Pre-ICO Payment")],
  ["squad_preico_forward", labelPair("Squirtle Squad Pre-ICO Forward", "Squirtle Squad Pre-ICO Forward")],
  ["squad_preico_claim", labelPair("Squirtle Squad Pre-ICO Claim", "Squirtle Squad Pre-ICO Claim")],
  ["vfox_nft_holder_reward", labelPair("VFA NFT Holder Reward", "VFA NFT Holder Reward")],
  ["vesea_nft_staking_rewards", labelPair("VeSea NFT Staking Rewards", "VeSea NFT Staking Rewards")],
  ["union_nft_holder_rewards", labelPair("UNION NFT Holder Rewards", "UNION NFT Holder Rewards")],
  ["vimworld_veed_reward_distribution", labelPair("VIMworld Reward Distribution", "VIMworld Reward Distribution")],
  ["vimworld_veed_deposit", labelPair("VIMworld VEED Deposit", "VIMworld VEED Deposit")],
  ["vimworld_veed_withdrawal", labelPair("VIMworld VEED Withdrawal", "VIMworld VEED Withdrawal")],
  ["vimworld_veed_feed_fee", labelPair("VIMworld VEED Feed Fee", "VIMworld VEED Feed Fee")],
  ["vimworld_veed_withdrawal_fee", labelPair("VIMworld VEED Withdrawal Fee", "VIMworld VEED Withdrawal Fee")],
  ["governance_deposit", labelPair("Governance Deposit", "Governance Deposit")],
  ["governance_refund", labelPair("Governance Refund", "Governance Refund")],
  ["governance_delegate_fee", labelPair("Governance Delegate Fee", "Governance Delegate Fee")],
  ["exchange_deposit", labelPair("Exchange Deposit", "Exchange Deposit")],
  ["exchange_withdrawal", labelPair("Exchange Withdrawal", "Exchange Withdrawal")],
  ["wanchain_bridge_burn_out", labelPair("WanBridge Bridge Burn Out", "WanBridge Bridge Burn Out")],
  ["wanchain_bridge_network_fee", labelPair("WanBridge Network Fee", "WanBridge Network Fee")],
  ["token_approval", labelPair("Token Approval", "Token Approval")],
  ["mint_payment", labelPair("NFT Mint Payment", "NFT Mint Payment")],
  ...JUICY_RAW_TYPE_FILTER_OPTIONS.map((opt) => [
    opt.value,
    labelPair(String(opt.label).replace(/^🟦\s*/, ""), String(opt.label).replace(/^🟦\s*/, "")),
  ]),
];

const TIER1_VALUE_SET = new Set(
  MANUAL_OVERRIDE_TIER1.map(([value]) => normalizeClassKey(value))
);

const TIER2_LABEL_BY_KEY = new Map(
  [...MANUAL_OVERRIDE_WRAP_TRANSFORMS, ...MANUAL_OVERRIDE_TIER2].map(([value, labels]) => [
    normalizeClassKey(value),
    labels,
  ])
);

/** Legacy/auto: airdrop → X2Earn Reward im Picker. */
export function resolveManualOverridePickerValue(rawValue) {
  const key = normalizeClassKey(rawValue);
  if (!key) return "auto";
  if (key === "airdrop") return "x2earn_reward";
  return key;
}

function pickLabel(labels, language) {
  const lang = language === "en" ? "en" : "de";
  return labels?.[lang] || labels?.en || labels?.de || "";
}

function formatDynamicClassificationLabel(classKey, language) {
  const known = TIER2_LABEL_BY_KEY.get(classKey);
  if (known) return pickLabel(known, language);

  return classKey
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) =>
      part.toLowerCase() === "vebetterdao"
        ? "VeBetterDAO"
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join(" ");
}

export function buildManualOverrideSelectGroups({
  language = "de",
  datasetClassKeys = [],
} = {}) {
  const lang = language === "en" ? "en" : "de";

  const tier1 = MANUAL_OVERRIDE_TIER1.map(([value, labels]) => ({
    value: normalizeClassKey(value),
    label: pickLabel(labels, lang),
  })).filter(({ value }) => !isManualOverrideClassBlocked(value));

  const reserved = new Set(TIER1_VALUE_SET);
  reserved.add("airdrop");

  const tierWrap = [];
  const tierWrapKeys = new Set();

  for (const [value, labels] of MANUAL_OVERRIDE_WRAP_TRANSFORMS) {
    const key = normalizeClassKey(value);
    if (!key || reserved.has(key) || tierWrapKeys.has(key)) continue;
    tierWrapKeys.add(key);
    reserved.add(key);
    tierWrap.push({
      value: key,
      label: pickLabel(labels, lang),
    });
  }

  const tier2Static = [];
  const tier2StaticKeys = new Set();

  for (const [value, labels] of MANUAL_OVERRIDE_TIER2) {
    const key = normalizeClassKey(value);
    if (!key || reserved.has(key) || tier2StaticKeys.has(key) || isManualOverrideClassBlocked(key)) continue;
    tier2StaticKeys.add(key);
    reserved.add(key);
    tier2Static.push({
      value: key,
      label: pickLabel(labels, lang),
    });
  }
  tier2Static.sort((a, b) => a.label.localeCompare(b.label, lang));

  const tier2Dynamic = [];
  for (const raw of datasetClassKeys) {
    const key = normalizeClassKey(raw);
    if (!key || key === "auto" || reserved.has(key)) continue;
    reserved.add(key);
    tier2Dynamic.push({
      value: key,
      label: formatDynamicClassificationLabel(key, lang),
    });
  }
  tier2Dynamic.sort((a, b) => a.label.localeCompare(b.label, lang));

  return {
    tier1,
    tierWrap,
    tier2Static,
    tier2Dynamic,
    groupLabels: {
      tier1:
        lang === "en" ? "Common corrections" : "Häufige Korrekturen",
      tierWrap:
        lang === "en" ? "Wrap / Unwrap" : "Wrap / Unwrap",
      tier2Static:
        lang === "en" ? "Protocol / specific" : "Protokoll / Spezifisch",
      tier2Dynamic:
        lang === "en" ? "In this dataset" : "In diesem Datensatz",
    },
  };
}

export function collectDatasetClassificationKeys(rows = []) {
  const keys = new Set();
  for (const tx of rows) {
    const raw = tx?.manualClassification || tx?.classification || tx?.type || "";
    const key = normalizeClassKey(raw);
    if (key && key !== "auto" && !isManualOverrideClassBlocked(key)) keys.add(key);
  }
  return [...keys];
}
